import { FC } from 'react';
import classNames from 'classnames';
import { PrismicRichText } from '@prismicio/react';
import { asText } from '@prismicio/client';
import {
  getSlicePadding,
  HeadingAsParagraph,
  htmlSerializerWithProps,
} from '@/utils';
import { Container, Icon, Link, PrismicImageWithAlt } from '@/components';
import { ImageRowWithHeadingAndButtonSlice as ImageRow } from '@/types';
import {
  useFilteredSliceItemsByLocationSlug,
  useShowByLocationSlug,
} from '@/hooks';

import style from './imagerowwithheadingandbutton.module.scss';

/* eslint-disable-next-line max-len */
const ImageRowWithHeadingAndButton: FC<ImageRow> = slice => {
  const { showContent } = useShowByLocationSlug(slice?.primary?.location_slug);
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);

  const paddingSection = slice?.primary?.padding_section;
  const marginBottom = slice?.primary?.margin_bottom;
  const backgroundColor = slice?.primary?.background;
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const title = asText(slice.primary?.title);

  const sizes =
    /* eslint-disable-next-line max-len */
    '(min-width: 2048px) 412px, (min-width: 1536px) 304px, (min-width: 1280px) 247px, (min-width: 1024px) 208px, (min-width: 420px) 50wv, 100wv';

  if (!slice?.primary?.active || !showContent) {
    return null;
  }

  return (
    <section
      className={classNames(
        style.image_row__section,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
      }}
    >
      <Container className={style.container}>
        <div className={style.content}>
          <div
            className={classNames(style.body, {
              [style.body__square_picture]: slice.variation === 'squarePicture',
            })}
          >
            {slice.variation === 'default' && (
              <HeadingAsParagraph
                className={style.body__text_heading}
                color={slice.primary?.heading_color as string}
                field={slice.primary?.heading}
              />
            )}
            {title && (
              <PrismicRichText
                components={htmlSerializerWithProps(
                  { color: slice.primary?.title_color },
                  style.body__title,
                )}
                field={slice?.primary?.title}
              />
            )}
            {slice.variation === 'squarePicture' && (
              <PrismicRichText
                components={{
                  paragraph: ({ children }) => (
                    <p
                      style={{
                        color: slice.primary?.description_color!,
                      }}
                    >
                      {children}
                    </p>
                  ),
                }}
                field={slice.primary?.description}
              />
            )}
            <div className={style.body__contents_images}>
              {filteredItems.map((img, index) => {
                return (
                  <figure
                    className={classNames(style.body__figure, {
                      /* eslint-disable-next-line max-len */
                      [style.body__figure_border_radius_under_sm]:
                        !slice.primary?.background,
                    })}
                    key={index}
                  >
                    <PrismicImageWithAlt
                      field={img.image}
                      fill
                      noImageContent={<Icon name="image" />}
                      showPlaceholderIfEmpty
                      sizes={sizes}
                      style={{
                        objectFit: 'cover',
                      }}
                    />
                  </figure>
                );
              })}
            </div>
            {slice.primary?.button_text && (
              <Link
                aria-label={`Go to ${slice.primary?.button_text}`}
                className={classNames({
                  [style.body__button_primary]:
                    slice.primary?.button_type === 'Button',
                })}
                href={slice.primary?.button_link ?? '/'}
                style={{
                  background: slice.primary?.button_background!,
                  color: slice.primary?.button_text_color!,
                }}
              >
                {slice.primary?.button_text}
              </Link>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default ImageRowWithHeadingAndButton;
